import { useState, useEffect } from "react";
import { subirArchivo, obtenerArchivosPorTramite, descargarArchivo, eliminarArchivo } from "../services/archivoService";
import { toast } from "react-toastify";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import DeleteIcon from "@mui/icons-material/Delete";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DownloadIcon from "@mui/icons-material/Download";
import useCustomModal from "../hooks/useCustomModal";
import CustomModal from "../hooks/CustomModal.js";

function CargaArchivos({ idTramite, onArchivosActualizados }) {
    const [archivos, setArchivos] = useState([]);
    const [subiendoArchivos, setSubiendoArchivos] = useState(false);
    const [dragActive, setDragActive] = useState(false);
    const [cargandoArchivos, setCargandoArchivos] = useState(true);
    const {  modalState, hideModal, showAlert, showSuccess, showError, showWarning, showConfirm } = useCustomModal();

    // Cargar archivos existentes cuando el componente se monta
    useEffect(() => {
        if (idTramite) {
            cargarArchivosExistentes();
        }
    }, [idTramite]);

    const cargarArchivosExistentes = async () => {
        if (!idTramite) return;

        try {
            setCargandoArchivos(true);
            console.log("🔄 Cargando archivos existentes...");
            const archivosBackend = await obtenerArchivosPorTramite(idTramite);

            const archivosMapeados = archivosBackend.map(archivo => ({
                id: `backend_${archivo.idArchivo}`,
                idArchivo: archivo.idArchivo,
                nombre: archivo.nombre,
                tamaño: (archivo.tamaño / (1024 * 1024)).toFixed(2) + " MB",
                estado: "subido",
                tipo: archivo.tipo,
                esDelBackend: true
            }));

            setArchivos(archivosMapeados);

            if (onArchivosActualizados) {
                onArchivosActualizados(archivosMapeados);
            }
        } catch (error) {
            console.error("❌ Error cargando archivos existentes:", error);
        } finally {
            setCargandoArchivos(false);
        }
    };

    // 👇 Funciones para Drag & Drop
    const handleDrag = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === "dragenter" || e.type === "dragover") {
            setDragActive(true);
        } else if (e.type === "dragleave") {
            setDragActive(false);
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);

        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            handleSeleccionarArchivos({ target: { files: e.dataTransfer.files } });
        }
    };

    const handleSeleccionarArchivos = (event) => {
        const files = Array.from(event.target.files || event.dataTransfer?.files || []);

        if (files.length === 0) return;

        // Validaciones (mantener igual)
        const archivosActuales = archivos.filter(a => !a.esDelBackend || a.estado === 'pendiente');
        if (archivosActuales.length + files.length > 10) {
            showWarning("Máximo 10 archivos permitidos.", "Límite de Archivos Excedido");
            return;
        }

        const archivosDemasiadoGrandes = files.filter(file => file.size > 50 * 1024 * 1024);
        if (archivosDemasiadoGrandes.length > 0) {
            showWarning("Algunos archivos superan el tamaño máximo de 50 MB.", "Archivos Demasiado Grandes");
            return;
        }

        const tiposPermitidos = ['.pdf', '.doc', '.docx', '.jpg', '.jpeg', '.png', '.zip'];
        const archivosNoPermitidos = files.filter(file => {
            const extension = '.' + file.name.split('.').pop().toLowerCase();
            return !tiposPermitidos.includes(extension);
        });

        if (archivosNoPermitidos.length > 0) {
            showWarning("Algunos archivos tienen tipos no permitidos. Use: PDF, DOC, DOCX, JPG, PNG, ZIP.", "Tipo de Archivo no Permitido");
            return;
        }

        const nuevosArchivos = files.map(file => ({
            id: `local_${Date.now()}_${Math.random()}`,
            file,
            nombre: file.name,
            tamaño: (file.size / (1024 * 1024)).toFixed(2) + " MB",
            estado: "pendiente",
            esDelBackend: false
        }));

        const nuevosArchivosCompletos = [...archivos, ...nuevosArchivos];
        setArchivos(nuevosArchivosCompletos);

        if (onArchivosActualizados) {
            onArchivosActualizados(nuevosArchivosCompletos);
        }

        if (event.target) event.target.value = "";
    };

    const handleEliminarArchivo = async (id) => {
        console.log("🗑️ handleEliminarArchivo llamado con ID:", id);

        const archivo = archivos.find(a => a.id === id);
        if (!archivo) {
            console.error("❌ Archivo no encontrado:", id);
            return;
        }

        // 🔥 USAR TU showConfirm - 100% FRONTEND
        showConfirm(
            `¿Está seguro de que desea eliminar el archivo "${archivo.nombre}"?\n\nEsta acción no se puede deshacer.`,
            async () => {
                try {
                    console.log("🗑️ Eliminando archivo:", archivo);

                    if (archivo.esDelBackend && archivo.idArchivo) {
                        console.log("🌐 Eliminando archivo del backend, ID:", archivo.idArchivo);
                        await eliminarArchivo(archivo.idArchivo);
                        toast.success(`Archivo "${archivo.nombre}" eliminado del servidor.`);
                    } else {
                        console.log("💻 Eliminando archivo local");
                        toast.info(`Archivo "${archivo.nombre}" eliminado.`);
                    }

                    const nuevosArchivos = archivos.filter(a => a.id !== id);
                    console.log("🔄 Actualizando estado, nuevos archivos:", nuevosArchivos.length);
                    setArchivos(nuevosArchivos);

                    if (onArchivosActualizados) {
                        onArchivosActualizados(nuevosArchivos);
                    }

                } catch (error) {
                    console.error("❌ Error eliminando archivo:", error);
                    showError("Error al eliminar el archivo del servidor.", "Error de Eliminación");
                }
            },
            'Confirmar Eliminación',
            'danger' // Esto hará que el botón sea rojo
        );
    };

    const handleLimpiarTodo = () => {
        if (archivos.length === 0) return;

        const archivosBackend = archivos.filter(a => a.esDelBackend);
        const archivosLocales = archivos.filter(a => !a.esDelBackend);

        let mensaje, titulo, tipoBoton;

        if (archivosBackend.length > 0 && archivosLocales.length > 0) {
            titulo = 'Eliminar Todos los Archivos';
            mensaje = `Esta acción eliminará:\n• ${archivosBackend.length} archivo(s) del servidor\n• ${archivosLocales.length} archivo(s) locales no subidos\n\n¿Desea continuar?`;
            tipoBoton = 'danger';
        } else if (archivosBackend.length > 0) {
            titulo = 'Eliminar Archivos del Servidor';
            mensaje = `Se eliminarán ${archivosBackend.length} archivo(s) del servidor permanentemente.\n\nEsta acción no se puede deshacer.`;
            tipoBoton = 'danger';
        } else {
            titulo = 'Limpiar Archivos Locales';
            mensaje = `Se eliminarán ${archivosLocales.length} archivo(s) locales no subidos.`;
            tipoBoton = 'warning';
        }

        showConfirm(
            mensaje,
            async () => {
                try {
                    setSubiendoArchivos(true);

                    if (archivosBackend.length > 0) {
                        console.log("🗑️ Eliminando archivos del backend:", archivosBackend);

                        for (const archivo of archivosBackend) {
                            if (archivo.idArchivo) {
                                try {
                                    console.log(`🌐 Eliminando archivo del backend: ${archivo.nombre} (ID: ${archivo.idArchivo})`);
                                    await eliminarArchivo(archivo.idArchivo);
                                    console.log(`✅ Eliminado del backend: ${archivo.nombre}`);
                                } catch (error) {
                                    console.error(`❌ Error eliminando ${archivo.nombre}:`, error);
                                }
                            }
                        }
                    }

                    console.log("🔄 Limpiando todos los archivos del estado local");
                    setArchivos([]);

                    if (onArchivosActualizados) {
                        onArchivosActualizados([]);
                    }

                    toast.success("Todos los archivos han sido eliminados.");

                } catch (error) {
                    console.error("❌ Error eliminando archivos:", error);
                    showError("Error al eliminar algunos archivos del servidor.", "Error de Eliminación");
                } finally {
                    setSubiendoArchivos(false);
                }
            }, titulo,tipoBoton
        );
    };

    const handleSubirArchivos = async () => {
        const archivosPendientes = archivos.filter(a => a.estado === 'pendiente' && !a.esDelBackend);

        if (archivosPendientes.length === 0) {
            showAlert("No hay archivos pendientes para subir.", "Sin Archivos para Subir");
            return;
        }

        setSubiendoArchivos(true);

        try {
            let archivosSubidos = 0;
            let archivosConError = 0;

            for (const archivo of archivosPendientes) {
                try {
                    setArchivos(prev => prev.map(item =>
                        item.id === archivo.id ? { ...item, estado: "subiendo" } : item
                    ));

                    const archivoSubido = await subirArchivo(idTramite, archivo.file);

                    setArchivos(prev => prev.map(item =>
                        item.id === archivo.id ? {
                            ...item,
                            estado: "subido",
                            idArchivo: archivoSubido.idArchivo,
                            esDelBackend: true
                        } : item
                    ));

                    archivosSubidos++;

                } catch (error) {
                    const errorMessage = error.message || "Error desconocido al subir el archivo";

                    setArchivos(prev => prev.map(item =>
                        item.id === archivo.id ? {
                            ...item,
                            estado: "error",
                            error: errorMessage
                        } : item
                    ));

                    archivosConError++;
                }
            }

            if (archivosConError === 0) {
                showSuccess(`Todos los archivos (${archivosSubidos}) se subieron correctamente.`, "Subida Exitosa");
                toast.success("Archivos subidos correctamente");
            } else if (archivosSubidos > 0) {
                showWarning(
                    `${archivosSubidos} archivo(s) subidos, ${archivosConError} con error.`,
                    "Subida Parcial"
                );
            } else {
                showError("No se pudo subir ningún archivo.", "Error de Subida");
            }

            await cargarArchivosExistentes();

        } catch (error) {
            console.error("❌ Error general al subir archivos:", error);
            showError(
                "Error al subir archivos: " + (error.message || "Error desconocido"),
                "Error General de Subida"
            );
        } finally {
            setSubiendoArchivos(false);
        }
    };

    const handleDescargarArchivo = async (archivo) => {
        try {
            if (archivo.esDelBackend && archivo.idArchivo) {
                const response = await descargarArchivo(archivo.idArchivo);

                const url = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', archivo.nombre);
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);

                toast.success(`Descargando ${archivo.nombre}`);
            } else if (archivo.file) {
                const url = URL.createObjectURL(archivo.file);
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', archivo.nombre);
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);
            } else {
                showError("No se puede descargar el archivo", "Error de Descarga");
            }
        } catch (error) {
            console.error("❌ Error descargando archivo:", error);
            showError("Error al descargar el archivo.", "Error de Descarga");
        }
    };

    const archivosPendientes = archivos.filter(a => !a.esDelBackend && a.estado === 'pendiente').length;

    const getIconoArchivo = (archivo) => {
        const nombre = archivo.nombre.toLowerCase();
        const tipo = archivo.tipo?.toLowerCase() || '';

        const iconos = {
            pdf: { icon: 'bi-file-earmark-pdf-fill', color: 'text-danger', label: 'Documento PDF' },
            doc: { icon: 'bi-file-earmark-word-fill', color: 'text-primary', label: 'Documento Word' },
            docx: { icon: 'bi-file-earmark-word-fill', color: 'text-primary', label: 'Documento Word' },
            xls: { icon: 'bi-file-earmark-excel-fill', color: 'text-success', label: 'Hoja de cálculo Excel' },
            xlsx: { icon: 'bi-file-earmark-excel-fill', color: 'text-success', label: 'Hoja de cálculo Excel' },
            jpg: { icon: 'bi-file-earmark-image-fill', color: 'text-warning', label: 'Imagen JPEG' },
            jpeg: { icon: 'bi-file-earmark-image-fill', color: 'text-warning', label: 'Imagen JPEG' },
            png: { icon: 'bi-file-earmark-image-fill', color: 'text-info', label: 'Imagen PNG' },
            gif: { icon: 'bi-file-earmark-image-fill', color: 'text-success', label: 'Imagen GIF' },
            zip: { icon: 'bi-file-earmark-zip-fill', color: 'text-secondary', label: 'Archivo comprimido' },
            rar: { icon: 'bi-file-earmark-zip-fill', color: 'text-secondary', label: 'Archivo comprimido' },
            ppt: { icon: 'bi-file-earmark-ppt-fill', color: 'text-danger', label: 'Presentación PowerPoint' },
            pptx: { icon: 'bi-file-earmark-ppt-fill', color: 'text-danger', label: 'Presentación PowerPoint' },
            txt: { icon: 'bi-file-earmark-text', color: 'text-muted', label: 'Archivo de texto' },
            csv: { icon: 'bi-file-earmark-spreadsheet', color: 'text-success', label: 'Archivo CSV' }
        };

        // Buscar por extensión
        const extension = nombre.split('.').pop();
        const formato = iconos[extension];

        if (formato) {
            return (
                <i
                    className={`bi ${formato.icon} ${formato.color}`}
                    title={formato.label}
                ></i>
            );
        }

        // Buscar por tipo MIME
        if (tipo.includes('pdf')) {
            return <i className="bi bi-file-earmark-pdf-fill text-danger" title="Documento PDF"></i>;
        }
        if (tipo.includes('word') || tipo.includes('document')) {
            return <i className="bi bi-file-earmark-word-fill text-primary" title="Documento Word"></i>;
        }
        if (tipo.includes('excel') || tipo.includes('spreadsheet')) {
            return <i className="bi bi-file-earmark-excel-fill text-success" title="Hoja de cálculo"></i>;
        }
        if (tipo.includes('image')) {
            return <i className="bi bi-file-earmark-image-fill text-warning" title="Imagen"></i>;
        }
        if (tipo.includes('zip') || tipo.includes('compressed')) {
            return <i className="bi bi-file-earmark-zip-fill text-secondary" title="Archivo comprimido"></i>;
        }

        // Icono por defecto
        return <i className="bi bi-file-earmark-fill text-muted" title="Archivo"></i>;
    };

    return (
        <div className="card shadow-lg border-0 rounded-4 p-4">
            <h4 className="fw-bold text-primary mb-3">
                Gestión de Archivos
            </h4>

            {/* Lista de archivos */}
            {archivos.length > 0 && (
                <div className="mb-4">
                    <h6 className="fw-semibold mb-3">
                        Archivos ({archivos.length}/10):
                        {cargandoArchivos && (
                            <small className="text-muted ms-2">
                                <div className="spinner-border spinner-border-sm" role="status"></div>
                                Cargando...
                            </small>
                        )}
                    </h6>

                    <div className="d-flex flex-column gap-2">
                        {archivos.map(archivo => {
                            const borderClass =
                                archivo.estado === 'subido'   ? 'border-success' :
                                archivo.estado === 'subiendo' ? 'border-warning' :
                                archivo.estado === 'error'    ? 'border-danger'  : 'border-secondary';

                            const badgeClass =
                                archivo.estado === 'subido'   ? 'bg-success' :
                                archivo.estado === 'subiendo' ? 'bg-warning text-dark' :
                                archivo.estado === 'error'    ? 'bg-danger'  : 'bg-secondary';

                            const badgeIcon =
                                archivo.estado === 'subiendo' ? <i className="bi bi-arrow-clockwise me-1"></i> :
                                archivo.estado === 'error'    ? <i className="bi bi-x-circle me-1"></i> :
                                archivo.estado === 'pendiente'? <i className="bi bi-clock me-1"></i> : null;

                            const badgeText =
                                archivo.estado === 'subido'   ? 'Subido'     :
                                archivo.estado === 'subiendo' ? 'Subiendo...' :
                                archivo.estado === 'error'    ? 'Error'      : 'Pendiente';

                            return (
                                <div
                                    key={archivo.id}
                                    className={`d-flex align-items-center justify-content-between border ${borderClass} rounded-3 px-3 py-2`}
                                    style={{ transition: 'background-color 0.15s ease' }}
                                    onMouseEnter={e => e.currentTarget.style.backgroundColor = '#f8fafc'}
                                    onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
                                >
                                    {/* Izquierda: ícono + nombre + tamaño + badge */}
                                    <div className="d-flex align-items-center gap-3 flex-grow-1" style={{ minWidth: 0 }}>
                                        <div style={{ fontSize: '1.5rem', flexShrink: 0 }}>
                                            {getIconoArchivo(archivo)}
                                        </div>
                                        <div style={{ minWidth: 0 }}>
                                            <div
                                                className="fw-semibold"
                                                style={{ wordBreak: 'break-word', cursor: 'pointer', textDecoration: 'underline', lineHeight: '1.4' }}
                                                onClick={() => handleDescargarArchivo(archivo)}
                                                title={archivo.nombre}
                                            >
                                                {archivo.nombre}
                                            </div>
                                            <div className="d-flex align-items-center gap-2 mt-1 flex-wrap">
                                                <small className="text-muted">{archivo.tamaño}</small>
                                                <span className={`badge ${badgeClass}`}>
                                                    {badgeIcon}
                                                    {badgeText}
                                                    {archivo.esDelBackend && <i className="bi bi-cloud-fill ms-1"></i>}
                                                </span>
                                            </div>
                                            {archivo.estado === 'error' && archivo.error && (
                                                <small className="text-danger d-block mt-1">
                                                    <i className="bi bi-exclamation-triangle me-1"></i>
                                                    {archivo.error}
                                                </small>
                                            )}
                                        </div>
                                    </div>

                                    {/* Derecha: botones de acción */}
                                    {(archivo.estado === 'subido' || archivo.estado === 'error' || archivo.estado === 'pendiente') && (
                                        <div className="d-flex align-items-center gap-1 ms-3 flex-shrink-0">
                                            {(archivo.estado === 'subido' || !archivo.esDelBackend) && (
                                                <button
                                                    className="btn btn-sm fw-bold"
                                                    onClick={e => { e.preventDefault(); e.stopPropagation(); handleDescargarArchivo(archivo); }}
                                                    title="Descargar archivo"
                                                    style={{ color: '#0056b3', border: 'none', fontSize: '1.25rem', padding: '4px 8px' }}
                                                >
                                                    <i className="bi bi-download"></i>
                                                </button>
                                            )}
                                            <button
                                                className="btn btn-sm fw-bold"
                                                onClick={e => { e.preventDefault(); e.stopPropagation(); handleEliminarArchivo(archivo.id); }}
                                                title="Eliminar archivo"
                                                style={{ color: '#dc3545', border: 'none', fontSize: '1.25rem', padding: '4px 8px' }}
                                            >
                                                <i className="bi bi-trash"></i>
                                            </button>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Resto del componente (área de drag & drop y botones) */}
            <div className="alert alert-info border-0 rounded-3">
                <small>
                    <strong>Límites:</strong> Tamaño máximo por archivo: 50 MB,
                    número máximo de archivos: 10
                </small>
                <br />
                <small>
                    <strong>Tipos de archivo aceptados:</strong> PDF, DOC, DOCX, JPG, PNG, ZIP
                </small>
            </div>

            <div
                className={`border rounded-4 p-5 text-center mb-4 cursor-pointer transition-all ${
                    dragActive
                        ? "border-primary bg-primary bg-opacity-10"
                        : "border-dashed border-muted bg-light"
                }`}
                style={{
                    borderStyle: dragActive ? 'solid' : 'dashed',
                    transition: 'all 0.3s ease'
                }}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                onClick={() => document.getElementById('file-input').click()}
            >
                <input
                    type="file"
                    multiple
                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.zip"
                    onChange={handleSeleccionarArchivos}
                    className="d-none"
                    id="file-input"
                />

                <CloudUploadIcon
                    fontSize="large"
                    className={`mb-3 ${dragActive ? 'text-primary' : 'text-muted'}`}
                    style={{ fontSize: '3rem' }}
                />

                <h5 className={dragActive ? 'text-primary' : 'text-muted'}>
                    {dragActive ? "¡Suelta los archivos aquí!" : "Haz clic o arrastra archivos aquí"}
                </h5>

                <p className="text-muted small mb-0">
                    Puedes seleccionar múltiples archivos
                </p>
            </div>

            {archivosPendientes > 0 && (
                <div className="d-flex justify-content-center gap-3">
                    <button
                        className="btn btn-primary d-flex align-items-center gap-2 rounded-pill px-4 py-2 fw-semibold"
                        onClick={handleSubirArchivos}
                        disabled={subiendoArchivos}
                    >
                        {subiendoArchivos ? (
                            <>
                                <div className="spinner-border spinner-border-sm" role="status"></div>
                                Subiendo...
                            </>
                        ) : (
                            <>
                                <CloudUploadIcon fontSize="small" />
                                Subir Archivos ({archivosPendientes})
                            </>
                        )}
                    </button>

                    <button
                        className="btn btn-outline-danger d-flex align-items-center gap-2 rounded-pill px-4 py-2 fw-semibold"
                        onClick={handleLimpiarTodo}
                        title="Eliminar todos los archivos"
                        disabled={subiendoArchivos}
                    >
                        <DeleteIcon fontSize="small" />
                        Limpiar Todo
                    </button>
                </div>
            )}
            <CustomModal
                {...modalState}
                onHide={hideModal}
                onConfirm={() => {
                    if (modalState.onConfirm) {
                        modalState.onConfirm();
                    }
                    hideModal();
                }}
            />
        </div>
    );
}

export default CargaArchivos;